import { useI18nContext } from "@i18n/i18n-react";
import { T } from "@i18n/T";
import { preloadQuery } from "@relay";
import { Button } from "@shadcn/ui/button";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { Container } from "lucide-react";
import { useState } from "react";
import { usePreloadedQuery } from "react-relay";
import { graphql } from "relay-runtime";
import { toast } from "sonner";
import { z } from "zod";
import { AnonymousLockAccessError } from "~/components/lock/AnonymousLockAccess";
import { formatConfigurationLabel, formatDimensions, getSizeBucket } from "~/components/lock/spaceConfiguration";
import { AppHeader } from "~/components/layout/AppHeader";
import { PageContainer } from "~/components/layout/PageContainer";
import { CollectedSummary } from "~/components/warenannahme/CollectedSummary";
import { SummaryRow } from "~/components/warenannahme/SummaryParts";
import { useHubGate } from "~/components/warenannahme/useHubGate";
import { compartmentSide, WarenannahmeDoorFlow } from "~/components/warenannahme/WarenannahmeDoorFlow";
import type { accessWarenannahmeQuery } from "./__generated__/accessWarenannahmeQuery.graphql";

export const query = graphql`
	query accessWarenannahmeQuery($accessToken: String!) @relay_test_operation {
		anonymousUtilizationGrant(accessToken: $accessToken) {
			id
			deactivatedAt
			permissions
			reservation {
				id
				contentNote
				note
				space {
					frame
					position
					configuration
					dimensions {
						widthMm
						heightMm
						depthMm
					}
					hub {
						id
						status
						spaces {
							frame
						}
						meta {
							id
							name
							street
							city
							postalCode
							lat
							lng
						}
					}
				}
			}
		}
	}
`;

export const Route = createFileRoute("/$hubMetaName/warenannahme/access")({
	beforeLoad: () => {
		document.title = "bacehub | Warenannahme";
	},
	component: RouteComponent,
	errorComponent: ({ error }) => <AnonymousLockAccessError error={error} service="LOCK_B2B" />,
	validateSearch: zodValidator(z.object({ token: z.string().min(1) })),
	loaderDeps: ({ search: { token } }) => ({ token }),
	loader: ({ context, deps }) => preloadQuery<accessWarenannahmeQuery>(context.relayEnvironment, query, { accessToken: deps.token }),
});

type Step = { type: "details" } | { type: "open" } | { type: "collected"; collectedAt: string };

/** Page an invite link of the goods reception leads to: collect (or deliver) without an account. */
function RouteComponent() {
	const i18n = useI18nContext().LL["new-ui"];
	const navigate = useNavigate();
	const { token } = Route.useSearch();
	const { anonymousUtilizationGrant: grant } = usePreloadedQuery<accessWarenannahmeQuery>(query, Route.useLoaderData());
	const [step, setStep] = useState<Step>({ type: "details" });
	const { space } = grant.reservation;
	const { hub } = space;
	const hubName = hub.meta?.name ?? "";
	const hubAddress = hub.meta ? `${hub.meta.street}, ${hub.meta.postalCode} ${hub.meta.city}` : "";
	const hubGate = useHubGate({
		id: hub.id,
		name: hubName,
		lat: hub.meta?.lat,
		lng: hub.meta?.lng,
		frames: hub.spaces.map((item) => item.frame),
	});
	const collects = grant.permissions.includes("END_ON_OPEN");
	const canOpen = grant.permissions.includes("UNLOCK");
	const compartmentNumber = `${space.frame}-${space.position}`;
	const content = grant.reservation.contentNote || null;
	const comment = grant.reservation.note || null;
	const isHubOnline = hub.status === "ONLINE";
	const isCollected = grant.deactivatedAt != null;

	if (step.type === "open") {
		return (
			<WarenannahmeDoorFlow
				hubId={hub.id}
				hubName={hubName}
				side={compartmentSide(
					hub.spaces.map((item) => item.frame),
					space.frame
				)}
				mode="collect"
				access={{ type: "anonymous", accessToken: token }}
				onDone={(outcome) => {
					if (collects) {
						setStep({ type: "collected", collectedAt: new Date().toISOString() });
						return;
					}
					if (outcome === "success") toast.success(i18n.WA_ACCESS_CLOSED());
					setStep({ type: "details" });
				}}
			/>
		);
	}

	if (step.type === "collected") {
		return (
			<CollectedSummary
				hubName={hubName}
				hubAddress={hubAddress}
				size={getSizeBucket(space.configuration) ?? ""}
				compartmentNumber={compartmentNumber}
				content={content}
				comment={comment}
				collectedAt={step.collectedAt}
				actionLabel={i18n.WA_ACCESS_DONE()}
				onAction={() => navigate({ to: "/" })}
			/>
		);
	}

	return (
		<PageContainer
			footer={
				canOpen && !isCollected ? (
					<Button
						size="lg"
						className="w-full shadow-xl"
						disabled={!isHubOnline}
						onClick={() => hubGate.run(() => setStep({ type: "open" }))}
					>
						<T>{collects ? i18n.WA_COLLECT_CTA() : i18n.WA_OPEN_CTA()}</T>
					</Button>
				) : null
			}
		>
			<AppHeader service="LOCK_B2B" />
			<div className="flex flex-col gap-6 px-6 pt-6">
				<div className="flex flex-col gap-2">
					<h1 className="text-2xl font-breit font-medium">
						<T>
							{isCollected ? i18n.WA_ACCESS_COLLECTED_TITLE() : collects ? i18n.WA_ACCESS_COLLECT_TITLE() : i18n.WA_ACCESS_OPEN_TITLE()}
						</T>
					</h1>
					<p className="text-sm text-muted-foreground">
						<T>{isCollected ? i18n.WA_ACCESS_COLLECTED_DESC() : collects ? i18n.WA_ACCESS_COLLECT_DESC() : i18n.WA_ACCESS_OPEN_DESC()}</T>
					</p>
				</div>
				{!isHubOnline && !isCollected ? (
					<p role="status" className="rounded-lg bg-muted px-4 py-3 text-sm text-muted-foreground">
						<T>{i18n.WA_HUB_OFFLINE()}</T>
					</p>
				) : null}

				<div className="flex flex-col gap-4 rounded-[14px] border bg-background p-6 shadow-md">
					<div className="flex min-w-0 flex-col gap-1">
						<p className="text-xl font-breit font-medium leading-none">{formatConfigurationLabel(space.configuration)}</p>
						<p className="text-base text-muted-foreground">{formatDimensions(space.dimensions)}</p>
					</div>
					<div className="flex flex-col gap-3">
						<div className="flex items-start justify-between gap-4">
							<span className="text-sm text-[#737373]">
								<T>{i18n.WA_CARD_COMPARTMENT()}</T>
							</span>
							<span className="text-sm font-medium">{i18n.WA_COMPARTMENT_NUMBER({ number: compartmentNumber })}</span>
						</div>
						{content ? (
							<div className="flex items-start justify-between gap-4">
								<span className="text-sm text-[#737373]">
									<T>{i18n.WA_CARD_CONTENT()}</T>
								</span>
								<span className="text-right text-sm font-medium break-words">{content}</span>
							</div>
						) : null}
						<div className="flex items-start justify-between gap-4">
							<span className="text-sm text-[#737373]">
								<T>{i18n.WA_CARD_COMMENT()}</T>
							</span>
							<span className="text-right text-sm font-medium break-words">{comment || "–"}</span>
						</div>
					</div>
				</div>

				<div className="flex flex-col">
					<SummaryRow icon={<Container className="size-6" />} title={hubName} description={hubAddress} />
				</div>
			</div>
			{hubGate.drawer}
		</PageContainer>
	);
}
